import {Injectable} from '@angular/core';
import {
  collection,
  collectionData,
  deleteField,
  doc,
  docData,
  Firestore,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import {documentId} from 'firebase/firestore';
import {CoreService} from '../../core/service';
import {
  DEFAULT_AUTO_ORDER_ORDER_ITEM,
  IAutoOrderFirestoreOrderItem,
  IAutoOrderOrder,
  IAutoOrderOrderItem,
  IAutoOrderOrderPreparedInfoDetails,
  IAutoOrderOrderStockItem,
  IAutoOrderSupplierEmails,
} from '../models';
import {combineLatest, Observable, of} from 'rxjs';
import {catchError, filter, map} from 'rxjs/operators';
import {encodeStrict} from '../../../shared/shared-utils/firestore/firestore.utils';
import {IMessage, MessageTypesEnum} from '../../core/models';
import {
  path_operational_stores_data_auto_ordering_auto_orders_order_id,
  path_operational_stores_data_auto_ordering_auto_orders_order_items_GET,
  path_operational_stores_data_storeId_messages_from_app,
} from '../../../shared/shared-services/database-paths';
import {createOperationalServerTimestamp} from '../../../shared/shared-utils/firestore/operational-server-timestamp.utils';
import {
  PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID_AT_ITEM_CODE,
} from '../../../shared/shared-services/database-paths.operational';
import {
  autoOrderItemDocMatchesCode,
  getAutoOrderItemDocIdCandidates,
  orderItemSubcollectionDocMatchesCode,
  resolveAutoOrderPreparedItemKey,
} from '../utils/auto-order-prepared-item-key.utils';

interface IAutoOrderFirestoreOrderWithLegacyItems extends IAutoOrderOrder {
  items?: Record<string, unknown>;
}

export const AUTO_ORDER_RETRY_NOT_VALID = 'AUTO_ORDER_RETRY_NOT_VALID';

@Injectable({providedIn: 'root'})
export class AutoOrderService extends CoreService {
  constructor(protected firestore: Firestore) {
    super(firestore);
  }

  async addStockItemsToOrder(
    storeId: string,
    orderId: string,
    stockItems: IAutoOrderOrderStockItem[],
    supplierId: string,
  ): Promise<void> {
    const batch = writeBatch(this.firestore);

    for (const s of stockItems) {
      const ref = doc(
        this.firestore,
        PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID_AT_ITEM_CODE(
          storeId,
          orderId,
          s.code,
        ),
      );

      const newItem: IAutoOrderOrderItem = {
        ...DEFAULT_AUTO_ORDER_ORDER_ITEM,
        new: 'ADDED',
        ...(s.regularSuppCode === supplierId && {mainSupplier: 1}),
      };

      batch.set(ref, newItem, {merge: true});
    }

    await batch.commit();
  }

  /**
   * Loads an auto order that is eligible to retry.
   * Missing documents and status-only records are rejected.
   * When requireRetryableFailure is true, generation (`status`) or IQ (`iqStatus`) must be FAILED.
   */
  async getEligibleRetryAutoOrder(
    storeId: string,
    orderId: string,
    requireRetryableFailure: boolean = true,
  ): Promise<IAutoOrderOrder> {
    const orderPath: string = path_operational_stores_data_auto_ordering_auto_orders_order_id(storeId, orderId);
    const snapshot = await getDoc(doc(this.firestore, orderPath));
    if (!snapshot.exists()) {
      throw new Error(AUTO_ORDER_RETRY_NOT_VALID);
    }
    const existingOrder: IAutoOrderOrder = {
      ...(snapshot.data() as IAutoOrderOrder),
      orderId: snapshot.id,
    };
    const isRetryableFailure: boolean =
      existingOrder.status === 'FAILED' || existingOrder.iqStatus === 'FAILED';
    if (!existingOrder.userId || (requireRetryableFailure && !isRetryableFailure)) {
      throw new Error(AUTO_ORDER_RETRY_NOT_VALID);
    }
    return existingOrder;
  }

  /**
   * Retries a failed auto order only when the parent document exists and is valid.
   * Writes the SUBMITTED status update and submission message in one batch so neither
   * can be created without the other, and never creates a missing order document.
   */
  async submitEligibleAutoOrderRetry(
    storeId: string,
    orderId: string,
    requireRetryableFailure: boolean = true,
  ): Promise<void> {
    const existingOrder: IAutoOrderOrder = await this.getEligibleRetryAutoOrder(
      storeId,
      orderId,
      requireRetryableFailure,
    );
    const orderRef = doc(
      this.firestore,
      path_operational_stores_data_auto_ordering_auto_orders_order_id(storeId, orderId),
    );
    const messageRef = doc(collection(this.firestore, path_operational_stores_data_storeId_messages_from_app(storeId)));
    const message: IMessage<string> = {
      payload: {data: [orderId]},
      sender: existingOrder.userId,
      timestamp: createOperationalServerTimestamp(),
      type: MessageTypesEnum.AUTO_ORDERS,
    };
    const batch = writeBatch(this.firestore);
    batch.update(orderRef, {status: 'SUBMITTED'});
    batch.set(messageRef, message);
    await batch.commit();
  }

  /**
   * Streams live updates for a single auto order document.
   */
  watchAutoOrderDocument(storeId: string, orderId: string): Observable<IAutoOrderOrder> {
    const orderRef = doc(
      this.firestore,
      path_operational_stores_data_auto_ordering_auto_orders_order_id(storeId, orderId),
    );
    return docData(orderRef).pipe(
      filter((data: Record<string, unknown> | undefined): data is Record<string, unknown> => !!data),
      map((data: Record<string, unknown>): IAutoOrderOrder => ({
        ...(data as unknown as IAutoOrderOrder),
        orderId,
      })),
    );
  }

  async removeStockItemFromOrder(path: string, codes: string[]): Promise<void> {
    const batch = writeBatch(this.firestore);
    for (const code of codes) {
      const ref = doc(this.firestore, `${path}/${code}`);
      batch.delete(ref);
    }
    await batch.commit();
  }

  /**
   * Removes failed items from an auto order document and its orderItems subcollection,
   * then returns an updated order snapshot for retry.
   */
  async removeFailedItemsFromAutoOrder(
    storeId: string,
    orderId: string,
    codesToRemove: string[],
    order: IAutoOrderOrder,
  ): Promise<IAutoOrderOrder> {
    if (codesToRemove.length === 0) {
      return order;
    }

    const orderPath: string = path_operational_stores_data_auto_ordering_auto_orders_order_id(storeId, orderId);
    const orderItemsPath: string = path_operational_stores_data_auto_ordering_auto_orders_order_items_GET(
      storeId,
      orderId,
    );
    const orderDocRef = doc(this.firestore, orderPath);
    const orderSnapshot = await getDoc(orderDocRef);

    if (!orderSnapshot.exists()) {
      throw new Error(`Auto order document not found at ${orderPath}`);
    }

    const firestoreOrder: IAutoOrderFirestoreOrderWithLegacyItems = {
      ...(orderSnapshot.data() as IAutoOrderFirestoreOrderWithLegacyItems),
      orderId,
    };
    const batch = writeBatch(this.firestore);

    const deletedSubcollectionRefPaths = new Set<string>();
    const codesDeletedFromSubcollection = new Set<string>();
    const orderItemsSnapshot = await getDocs(collection(this.firestore, orderItemsPath));

    orderItemsSnapshot.forEach((itemDoc) => {
      const itemData: Record<string, unknown> = itemDoc.data() as Record<string, unknown>;
      const matchesCode: boolean = codesToRemove.some(
        (code: string) => orderItemSubcollectionDocMatchesCode(itemDoc.id, itemData, code),
      );
      if (matchesCode) {
        deletedSubcollectionRefPaths.add(itemDoc.ref.path);
        batch.delete(itemDoc.ref);
        codesToRemove.forEach((code: string) => {
          if (orderItemSubcollectionDocMatchesCode(itemDoc.id, itemData, code)) {
            codesDeletedFromSubcollection.add(code);
          }
        });
      }
    });

    codesToRemove
      .filter((code: string) => !codesDeletedFromSubcollection.has(code))
      .forEach((code: string) => {
        getAutoOrderItemDocIdCandidates(code).forEach((docIdSegment: string) => {
          const itemRef = doc(this.firestore, `${orderItemsPath}/${docIdSegment}`);
          if (!deletedSubcollectionRefPaths.has(itemRef.path)) {
            deletedSubcollectionRefPaths.add(itemRef.path);
            batch.delete(itemRef);
          }
        });
      });

    const existingPreparedItems: Record<string, IAutoOrderOrderPreparedInfoDetails> = {
      ...(firestoreOrder.preparedInfo?.preparedItems ?? {}),
    };
    const preparedItems: Record<string, IAutoOrderOrderPreparedInfoDetails> = {...existingPreparedItems};
    const preparedItemKeysRemoved: string[] = [];

    codesToRemove.forEach((code: string) => {
      const key: string | undefined = resolveAutoOrderPreparedItemKey(
        preparedItems as Record<string, unknown>,
        code,
      );
      if (key) {
        delete preparedItems[key];
        preparedItemKeysRemoved.push(key);
      }
    });

    let updatedLegacyItems: Record<string, unknown> | undefined;
    if (firestoreOrder.items) {
      updatedLegacyItems = {...firestoreOrder.items};
      codesToRemove.forEach((code: string) => {
        const key: string | undefined = resolveAutoOrderPreparedItemKey(updatedLegacyItems, code);
        if (key) {
          delete updatedLegacyItems[key];
        }
      });
    }

    const existingOnHold: string[] = Array.isArray(firestoreOrder.onHold) ? firestoreOrder.onHold : [];
    const updatedOnHold: string[] = existingOnHold.filter(
      (holdCode: string) => !codesToRemove.some(
        (code: string) => holdCode === code || autoOrderItemDocMatchesCode(holdCode, code),
      ),
    );

    let total = 0;
    Object.values(preparedItems).forEach((details: IAutoOrderOrderPreparedInfoDetails) => {
      const qty: number = details.qty ?? 0;
      const price: number = details.price ?? 0;
      if (qty > 0) {
        total += qty * price;
      }
    });

    const dirty: Record<string, boolean> | undefined = firestoreOrder.preparedInfo?.dirty
      ? {...firestoreOrder.preparedInfo.dirty}
      : undefined;
    if (dirty) {
      codesToRemove.forEach((code: string) => {
        const key: string | undefined = resolveAutoOrderPreparedItemKey(
          dirty as Record<string, unknown>,
          code,
        );
        if (key) {
          delete dirty[key];
        }
      });
    }

    const sourceOrderItems: IAutoOrderFirestoreOrderItem[] = Array.isArray(firestoreOrder.orderItems)
      ? firestoreOrder.orderItems
      : (Array.isArray(order.orderItems) ? order.orderItems : []);
    const updatedOrderItems: IAutoOrderFirestoreOrderItem[] = sourceOrderItems.filter(
      (item: IAutoOrderFirestoreOrderItem) => {
        if (!item.code) {
          return true;
        }
        return !codesToRemove.some(
          (code: string) => autoOrderItemDocMatchesCode(item.code, code),
        );
      },
    );

    const orderDocUpdate: Record<string, unknown> = {
      orderItems: updatedOrderItems,
      onHold: updatedOnHold,
      error: deleteField(),
      itemErrors: deleteField(),
      errorMsg: deleteField(),
      iqStatus: 'READY',
    };

    if (updatedLegacyItems !== undefined) {
      orderDocUpdate['items'] = updatedLegacyItems;
    }

    preparedItemKeysRemoved.forEach((key: string) => {
      orderDocUpdate[`preparedInfo.preparedItems.${key}`] = deleteField();
    });

    if (firestoreOrder.preparedInfo || Object.keys(existingPreparedItems).length > 0) {
      orderDocUpdate['preparedInfo'] = {
        ...firestoreOrder.preparedInfo,
        preparedItems,
        total,
        ...(dirty !== undefined ? {dirty} : {}),
      };
    }

    batch.update(orderDocRef, orderDocUpdate);
    await batch.commit();

    const verifyOrderSnapshot = await getDoc(orderDocRef);
    const verifyPreparedItems: Record<string, unknown> = (
      verifyOrderSnapshot.data()?.['preparedInfo'] as { preparedItems?: Record<string, unknown> } | undefined
    )?.preparedItems ?? {};
    const verifyRemovedCodesStillInPreparedInfo: string[] = codesToRemove.filter(
      (code: string) => resolveAutoOrderPreparedItemKey(verifyPreparedItems, code) != null,
    );
    const verifySubcollectionSnapshot = await getDocs(collection(this.firestore, orderItemsPath));
    const verifyRemainingCodesForRemoved: string[] = codesToRemove.filter((code: string) =>
      verifySubcollectionSnapshot.docs.some((itemDoc) =>
        orderItemSubcollectionDocMatchesCode(
          itemDoc.id,
          itemDoc.data() as Record<string, unknown>,
          code,
        ),
      ),
    );

    if (verifyRemovedCodesStillInPreparedInfo.length > 0 || verifyRemainingCodesForRemoved.length > 0) {
      throw new Error(
        'Failed to remove all items from the order before retry. '
        + `Still in preparedInfo: ${verifyRemovedCodesStillInPreparedInfo.join(', ') || 'none'}. `
        + `Still in orderItems subcollection: ${verifyRemainingCodesForRemoved.join(', ') || 'none'}.`,
      );
    }

    return {
      ...order,
      ...firestoreOrder,
      orderId,
      orderItems: updatedOrderItems,
      onHold: updatedOnHold,
      preparedInfo: firestoreOrder.preparedInfo
        ? {
          ...firestoreOrder.preparedInfo,
          preparedItems,
          total,
          ...(dirty !== undefined ? {dirty} : {}),
        }
        : order.preparedInfo,
      error: null,
      itemErrors: null,
      errorMsg: null,
      iqStatus: 'READY',
    };
  }

  async updateDocumentField<DataType extends object, NewValueType>(
    path: string,
    fieldName: string,
    newValue: NewValueType,
  ): Promise<void> {
    const ref = doc(this.firestore, path);
    const updateObj = {[fieldName]: newValue} as Partial<DataType>;
    await setDoc(ref, updateObj, {merge: true});
  }

  getSupplierEmails(
    path: string,
    supplierIds: string[],
  ): Observable<IAutoOrderSupplierEmails[]> {
    if (!supplierIds?.length) return of([]);

    const validIds = supplierIds.filter(Boolean).map(encodeStrict);
    const queries: Observable<IAutoOrderSupplierEmails[]>[] = [];

    // Firestore 'in' max 10 values
    while (validIds.length) {
      const chunk = validIds.splice(0, 10);
      const col = collection(this.firestore, path);
      const q = query(col, where(documentId(), 'in', chunk));
      const obs = collectionData(q, {idField: 'id'}) as Observable<
        IAutoOrderSupplierEmails[]
      >;
      queries.push(obs);
    }

    return combineLatest(queries).pipe(
      map((parts) => {
        const all = ([] as IAutoOrderSupplierEmails[]).concat(...parts);
        return supplierIds.map((rawId) => {
          const enc = encodeStrict(rawId);
          const found = all.find((d) => d.id === enc);
          return {
            ...found,
            id: rawId,
            additional: found?.additional?.length ? found.additional : [],
            selected: found?.selected?.length ? found.selected : [],
          } as IAutoOrderSupplierEmails;
        });
      }),
      catchError((err) => {
        throw err;
      }),
    );
  }
}
